import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { RefreshCw, DollarSign, Loader2, Search, Trash2, AlertCircle, CheckCircle, XCircle, Clock, BarChart3, ArrowUpRight, Shield, Mail, Hourglass, Gift, Activity, Copy } from 'lucide-react';
import axios from 'axios';
import { formatDate, formatDateTime, isoLocalDate, tzAbbrev } from '../../../utils/formatDate';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const StatusBadge = ({ status }) => {
  const colors = {
    completed: 'bg-green-100 text-green-700',
    pending: 'bg-amber-100 text-amber-700',
    expired: 'bg-gray-200 text-gray-600',
    failed: 'bg-red-100 text-red-700',
    cancelled: 'bg-gray-100 text-gray-500',
    refunded: 'bg-purple-100 text-purple-700',
  };
  return <span className={`text-[9px] font-semibold px-2 py-0.5 rounded ${colors[status] || 'bg-gray-100 text-gray-500'}`}>{status}</span>;
};

// Classify a donation's dedication_kit into one of: 'mens' | 'womens' | 'childrens' | null
const classifyKit = (d) => {
  const raw = (d?.dedication_kit || '').toLowerCase();
  if (!raw) return null;
  if (raw.includes('child')) return 'childrens';
  if (raw.includes('women') || raw.includes("women's") || raw.includes('woman')) return 'womens';
  if (raw.includes('men')) return 'mens';
  return null;
};

const KIT_META = {
  mens:      { label: "Men's Hygiene Kits",      color: 'text-blue-500',  dot: 'bg-blue-500'  },
  womens:    { label: "Women's Hygiene Kits",    color: 'text-pink-500',  dot: 'bg-pink-500'  },
  childrens: { label: "Children's Hygiene Kits", color: 'text-green-600', dot: 'bg-green-600' },
};

const KIT_FILTERS = ['kit_all', 'kit_mens', 'kit_womens', 'kit_childrens'];

const AdminDonationsTab = () => {
  const [donations, setDonations] = useState([]);
  const [reconciliation, setReconciliation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reconLoading, setReconLoading] = useState(false);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [showRecon, setShowRecon] = useState(false);
  const [diagnostics, setDiagnostics] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const [showDiag, setShowDiag] = useState(false);

  const fetchDiagnostics = async () => {
    setDiagLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/deploy/diagnostics`, { withCredentials: true });
      setDiagnostics(res.data);
      setShowDiag(true);
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to load diagnostics');
    }
    setDiagLoading(false);
  };

  const sendTestAlert = async () => {
    if (!window.confirm('Send a TEST alert email to the admin email? This is for verifying the alert channel works.')) return;
    try {
      const r = await axios.post(`${BACKEND_URL}/api/admin/webhook-alerts/test`, {}, { withCredentials: true });
      alert(`Test alert sent to ${r.data.to}`);
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to send test alert');
    }
  };

  const sendTestDigest = async () => {
    if (!window.confirm('Send a test daily digest email NOW (bypasses "already sent today" check)?')) return;
    try {
      const r = await axios.post(`${BACKEND_URL}/api/admin/digest/test`, {}, { withCredentials: true });
      alert(`Daily digest sent to ${r.data.to} for ${r.data.date}`);
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to send digest');
    }
  };

  const copyToClipboard = async (text) => {
    try { await navigator.clipboard.writeText(text); alert('Copied to clipboard'); }
    catch { alert('Copy failed — please select and copy manually'); }
  };

  const fetchDonations = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/donations`, { withCredentials: true });
      setDonations(res.data);
    } catch {}
    setLoading(false);
  }, []);

  const fetchReconciliation = async () => {
    setReconLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/donations/reconciliation`, { withCredentials: true });
      setReconciliation(res.data);
      setShowRecon(true);
    } catch { alert('Reconciliation failed'); }
    setReconLoading(false);
  };

  const handleCleanup = async (dryRun) => {
    const msg = dryRun
      ? 'Preview which test records would be removed?'
      : 'This will PERMANENTLY remove flagged test records (backed up first). Proceed?';
    if (!window.confirm(msg)) return;
    setCleanupLoading(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/donations/cleanup`, { dry_run: dryRun }, { withCredentials: true });
      if (dryRun) {
        alert(`Found ${res.data.donations_to_remove} test donations and ${res.data.orders_to_remove} test orders to remove.\n\nRun again with "Remove" to delete them (backup created automatically).`);
      } else {
        alert(`Removed ${res.data.donations_removed} donations and ${res.data.orders_removed} orders.\nBacked up to cleanup_backups collection.`);
        fetchDonations();
        fetchReconciliation();
      }
    } catch { alert('Cleanup failed'); }
    setCleanupLoading(false);
  };

  const handleExpirePending = async () => {
    if (!window.confirm('Expire all pending donations older than 48 hours?')) return;
    setActionLoading('expire');
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/donations/expire-pending`, {}, { withCredentials: true });
      alert(res.data.message);
      fetchDonations();
    } catch { alert('Failed'); }
    setActionLoading(null);
  };

  const handleVerifyStripe = async (donationId) => {
    setActionLoading(`verify-${donationId}`);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/donations/${donationId}/verify-stripe`, {}, { withCredentials: true });
      alert(res.data.message);
      fetchDonations();
    } catch (e) { alert(e.response?.data?.detail || 'Verification failed'); }
    setActionLoading(null);
  };

  const handleSendReminder = async (donationId) => {
    setActionLoading(`remind-${donationId}`);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/donations/${donationId}/send-reminder`, {}, { withCredentials: true });
      alert(res.data.message);
      fetchDonations();
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
    setActionLoading(null);
  };

  const handleResendReceipt = async (donation) => {
    // Admin-triggered resend for a completed donation of any age.
    // If the donor has an email on file, ask the admin to confirm
    // (with the option to override). If NOT on file, prompt for one.
    let overrideEmail = null;
    const onFile = (donation.donor_email || '').trim();
    if (!onFile) {
      const entered = window.prompt(
        `${donation.donor_name || 'This donor'} didn't leave an email address ` +
        `with their donation. Enter the email you want to send this receipt to:`);
      if (!entered) return;
      overrideEmail = entered.trim();
      if (!overrideEmail.includes('@')) {
        alert('That doesn\'t look like a valid email.');
        return;
      }
    }
    const donationId = donation.id;
    setActionLoading(`resend-${donationId}`);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/receipts/${donationId}/resend`,
        overrideEmail ? { email: overrideEmail } : {},
        { withCredentials: true });
      alert(res.data.status === 'sent'
        ? `Receipt emailed to ${res.data.donor_email}.`
        : 'Receipt sent.');
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to resend receipt.');
    }
    setActionLoading(null);
  };

  const handleBulkReminders = async () => {
    if (!window.confirm('Send reminder emails to all pending donors who haven\'t received one yet?')) return;
    setActionLoading('bulk-remind');
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/donations/bulk-send-reminders`, {}, { withCredentials: true });
      alert(res.data.message);
      fetchDonations();
    } catch { alert('Failed'); }
    setActionLoading(null);
  };

  useEffect(() => { fetchDonations(); }, [fetchDonations]);

  // Per-kit donation analytics (completed only; monthly + all-time)
  // "This month" is evaluated in the platform display zone (ET) so admin
  // browsers in other regions see the same month totals.
  const kitStats = useMemo(() => {
    const startOfMonthLocal = isoLocalDate(new Date()).slice(0, 7) + '-01';
    const empty = () => ({ mens: { count: 0, revenue: 0 }, womens: { count: 0, revenue: 0 }, childrens: { count: 0, revenue: 0 } });
    const monthly = empty();
    const allTime = empty();
    for (const d of donations) {
      if (d.status !== 'completed') continue;
      const kit = classifyKit(d);
      if (!kit) continue;
      const amt = Number(d.amount) || 0;
      allTime[kit].count += 1;
      allTime[kit].revenue += amt;
      const dLocal = d.created_at ? isoLocalDate(d.created_at) : '';
      if (dLocal && dLocal >= startOfMonthLocal) {
        monthly[kit].count += 1;
        monthly[kit].revenue += amt;
      }
    }
    const sum = (obj) => ({
      count: obj.mens.count + obj.womens.count + obj.childrens.count,
      revenue: obj.mens.revenue + obj.womens.revenue + obj.childrens.revenue,
    });
    return { monthly, allTime, monthlyTotal: sum(monthly), allTimeTotal: sum(allTime) };
  }, [donations]);

  const filtered = donations.filter(d => {
    const kit = classifyKit(d);
    const matchFilter =
      filter === 'all' ? true :
      filter === 'completed' ? d.status === 'completed' :
      filter === 'pending' ? d.status === 'pending' :
      filter === 'expired' ? d.status === 'expired' :
      filter === 'failed' ? ['failed', 'cancelled', 'refunded'].includes(d.status) :
      filter === 'test' ? (d.donor_name || '').toLowerCase().includes('test') || (d.donor_email || '').toLowerCase().includes('test') || (d.donor_email || '').includes('example.com') :
      filter === 'kit_all' ? !!kit :
      filter === 'kit_mens' ? kit === 'mens' :
      filter === 'kit_womens' ? kit === 'womens' :
      filter === 'kit_childrens' ? kit === 'childrens' :
      true;
    const matchSearch = !search || [d.donor_name, d.donor_email, d.id, d.source_label, d.allocation, d.dedication_kit].filter(Boolean).some(s => s.toLowerCase().includes(search.toLowerCase()));
    return matchFilter && matchSearch;
  });

  const r = reconciliation;
  const fin = r?.financials || {};
  const ds = r?.donation_summary || {};

  return (
    <div className="space-y-4" data-testid="donations-tab">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-heading text-lg font-bold text-[#0A0A0A]">Donations</h2>
          <p className="text-xs text-[#4B5563]">Track, verify, and manage all donation records.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={fetchDonations} disabled={loading}
            className="text-xs bg-[#F8F9FA] border border-[#E5E7EB] rounded px-3 py-1.5 hover:bg-[#F1F5F9] flex items-center gap-1 disabled:opacity-50"
            data-testid="refresh-donations-btn">
            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Refresh
          </button>
          <button onClick={fetchReconciliation} disabled={reconLoading}
            className="text-xs bg-[#0A0A0A] text-white rounded px-3 py-1.5 hover:bg-[#1a1a1a] flex items-center gap-1 disabled:opacity-50"
            data-testid="run-reconciliation-btn">
            {reconLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <BarChart3 className="w-3 h-3" />} Run Reconciliation
          </button>
          <button onClick={fetchDiagnostics} disabled={diagLoading}
            className="text-xs bg-blue-600 text-white rounded px-3 py-1.5 hover:bg-blue-700 flex items-center gap-1 disabled:opacity-50"
            data-testid="webhook-diagnostics-btn">
            {diagLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Activity className="w-3 h-3" />} Webhook Diagnostics
          </button>
          <button onClick={() => handleCleanup(true)} disabled={cleanupLoading}
            className="text-xs bg-amber-50 border border-amber-200 text-amber-700 rounded px-3 py-1.5 hover:bg-amber-100 flex items-center gap-1 disabled:opacity-50"
            data-testid="preview-cleanup-btn">
            <AlertCircle className="w-3 h-3" /> Preview Cleanup
          </button>
          <button onClick={() => handleCleanup(false)} disabled={cleanupLoading}
            className="text-xs bg-red-50 border border-red-200 text-red-700 rounded px-3 py-1.5 hover:bg-red-100 flex items-center gap-1 disabled:opacity-50"
            data-testid="run-cleanup-btn">
            {cleanupLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />} Remove Test Data
          </button>
          <button onClick={handleExpirePending} disabled={actionLoading === 'expire'}
            className="text-xs bg-gray-50 border border-gray-200 text-gray-600 rounded px-3 py-1.5 hover:bg-gray-100 flex items-center gap-1 disabled:opacity-50"
            data-testid="expire-pending-btn">
            {actionLoading === 'expire' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Hourglass className="w-3 h-3" />} Expire Old Pending
          </button>
          <button onClick={handleBulkReminders} disabled={actionLoading === 'bulk-remind'}
            className="text-xs bg-blue-50 border border-blue-200 text-blue-700 rounded px-3 py-1.5 hover:bg-blue-100 flex items-center gap-1 disabled:opacity-50"
            data-testid="bulk-reminders-btn">
            {actionLoading === 'bulk-remind' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />} Send Pending Reminders
          </button>
        </div>
      </div>

      {/* Reconciliation Report */}
      {showRecon && r && (
        <div className="bg-[#0A0A0A] rounded-xl p-5 space-y-4" data-testid="reconciliation-report">
          <div className="flex items-center justify-between">
            <h3 className="font-heading text-sm font-bold text-white flex items-center gap-2">
              <Shield className="w-4 h-4 text-[#BA1826]" /> Reconciliation Report
            </h3>
            <button onClick={() => setShowRecon(false)} className="text-xs text-gray-400 hover:text-white">Close</button>
          </div>

          {/* Financial Summary */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div className="bg-white/10 rounded-lg p-3 text-center">
              <div className="text-lg font-bold text-green-400">${fin.total_received?.toLocaleString()}</div>
              <p className="text-[10px] text-gray-400">Total Received</p>
            </div>
            <div className="bg-white/10 rounded-lg p-3 text-center">
              <div className="text-lg font-bold text-purple-400">${fin.total_refunded?.toLocaleString()}</div>
              <p className="text-[10px] text-gray-400">Refunded</p>
            </div>
            <div className="bg-white/10 rounded-lg p-3 text-center">
              <div className="text-lg font-bold text-red-400">${fin.total_failed_unpaid?.toLocaleString()}</div>
              <p className="text-[10px] text-gray-400">Failed / Unpaid</p>
            </div>
            <div className="bg-white/10 rounded-lg p-3 text-center">
              <div className="text-lg font-bold text-amber-400">${fin.total_test_removed?.toLocaleString()}</div>
              <p className="text-[10px] text-gray-400">Test Data</p>
            </div>
            <div className="bg-white/10 rounded-lg p-3 text-center border border-green-500/30">
              <div className="text-lg font-bold text-white">${fin.net_valid_donations?.toLocaleString()}</div>
              <p className="text-[10px] text-green-400 font-semibold">Net Valid Donations</p>
            </div>
          </div>

          {/* Record Counts */}
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
            {[
              { label: 'Total Records', value: ds.total_records, color: 'text-white' },
              { label: 'Completed', value: ds.completed, color: 'text-green-400' },
              { label: 'Pending', value: ds.pending, color: 'text-amber-400' },
              { label: 'Failed/Cancelled', value: ds.failed_cancelled_refunded, color: 'text-red-400' },
              { label: 'Test Records', value: ds.test_records, color: 'text-orange-400' },
              { label: 'Real Completed', value: ds.real_completed, color: 'text-emerald-400' },
            ].map(item => (
              <div key={item.label} className="text-center">
                <div className={`text-sm font-bold ${item.color}`}>{item.value || 0}</div>
                <p className="text-[9px] text-gray-500">{item.label}</p>
              </div>
            ))}
          </div>

          {/* By Source + By Allocation */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <p className="text-[10px] font-bold text-gray-400 uppercase mb-2">By Source</p>
              {Object.entries(r.by_source || {}).map(([src, data]) => (
                <div key={src} className="flex items-center justify-between text-xs text-white py-1 border-b border-white/5">
                  <span className="flex items-center gap-1"><ArrowUpRight className="w-3 h-3 text-[#BA1826]" />{src}</span>
                  <span>{data.count} donations — <span className="text-green-400 font-semibold">${data.total.toLocaleString()}</span></span>
                </div>
              ))}
              {Object.keys(r.by_source || {}).length === 0 && <p className="text-xs text-gray-500">No completed donations</p>}
            </div>
            <div>
              <p className="text-[10px] font-bold text-gray-400 uppercase mb-2">By Allocation / Fund</p>
              {Object.entries(r.by_allocation || {}).map(([alloc, data]) => (
                <div key={alloc} className="flex items-center justify-between text-xs text-white py-1 border-b border-white/5">
                  <span>{alloc}</span>
                  <span>{data.count} — <span className="text-green-400 font-semibold">${data.total.toLocaleString()}</span></span>
                </div>
              ))}
              {Object.keys(r.by_allocation || {}).length === 0 && <p className="text-xs text-gray-500">No allocations</p>}
            </div>
          </div>

          {/* Shop Summary */}
          <div className="border-t border-white/10 pt-3">
            <p className="text-[10px] font-bold text-gray-400 uppercase mb-2">Shop Orders</p>
            <div className="grid grid-cols-4 gap-2 text-center">
              <div><div className="text-sm font-bold text-white">{r.shop_summary?.total_orders || 0}</div><p className="text-[9px] text-gray-500">Total</p></div>
              <div><div className="text-sm font-bold text-green-400">{r.shop_summary?.completed_orders || 0}</div><p className="text-[9px] text-gray-500">Completed</p></div>
              <div><div className="text-sm font-bold text-white">${r.shop_summary?.shop_revenue?.toLocaleString() || 0}</div><p className="text-[9px] text-gray-500">Revenue</p></div>
              <div><div className="text-sm font-bold text-orange-400">{r.shop_summary?.test_orders || 0}</div><p className="text-[9px] text-gray-500">Test Orders</p></div>
            </div>
          </div>

          {/* Flagged Test Records */}
          {(r.flagged_test_donations?.length > 0 || r.flagged_test_orders?.length > 0) && (
            <div className="border-t border-white/10 pt-3">
              <p className="text-[10px] font-bold text-orange-400 uppercase mb-2">Flagged Test Records</p>
              {r.flagged_test_donations?.map(d => (
                <div key={d.id} className="text-[10px] text-gray-400 py-0.5 flex justify-between">
                  <span>{d.donor_name || 'Anonymous'} — ${d.amount} ({d.status})</span>
                  <span className="text-orange-400">{d.reason}</span>
                </div>
              ))}
              {r.flagged_test_orders?.map(o => (
                <div key={o.id} className="text-[10px] text-gray-400 py-0.5 flex justify-between">
                  <span>Shop: {o.buyer_name || 'Anonymous'} — ${o.total} ({o.status})</span>
                  <span className="text-orange-400">{o.reason}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Deploy / Webhook Diagnostics Panel */}
      {showDiag && diagnostics && (
        <div className="bg-white border-2 border-blue-200 rounded-xl p-5 space-y-4" data-testid="webhook-diagnostics-panel">
          <div className="flex items-center justify-between">
            <h3 className="font-heading text-sm font-bold text-[#0A0A0A] flex items-center gap-2">
              <Activity className="w-4 h-4 text-blue-600" /> Deploy & Webhook Diagnostics
            </h3>
            <button onClick={() => setShowDiag(false)} className="text-xs text-[#9CA3AF] hover:text-[#0A0A0A]">Close</button>
          </div>

          {/* Readiness score */}
          <div className="flex items-center gap-3">
            <div className={`text-2xl font-bold ${diagnostics.readiness.score_pct >= 85 ? 'text-green-600' : diagnostics.readiness.score_pct >= 60 ? 'text-amber-500' : 'text-red-600'}`}>
              {diagnostics.readiness.score_pct}%
            </div>
            <div className="flex-1">
              <div className="text-xs font-semibold text-[#0A0A0A]">
                Production Readiness — {diagnostics.readiness.passed} of {diagnostics.readiness.total} checks passing
              </div>
              <div className="w-full h-1.5 bg-[#F1F5F9] rounded-full overflow-hidden mt-1">
                <div className={`h-full transition-all ${diagnostics.readiness.score_pct >= 85 ? 'bg-green-500' : diagnostics.readiness.score_pct >= 60 ? 'bg-amber-500' : 'bg-red-500'}`}
                  style={{ width: `${diagnostics.readiness.score_pct}%` }} />
              </div>
            </div>
          </div>

          {/* Stripe webhook url + mode */}
          <div className="grid md:grid-cols-3 gap-3 text-xs">
            <div className="border border-[#E5E7EB] rounded p-3">
              <p className="text-[10px] uppercase tracking-wider text-[#9CA3AF] mb-1">Stripe Mode</p>
              <p className={`font-bold text-sm ${diagnostics.stripe.mode === 'LIVE' ? 'text-red-600' : diagnostics.stripe.mode === 'TEST' ? 'text-amber-500' : 'text-[#9CA3AF]'}`}>
                {diagnostics.stripe.mode}
              </p>
            </div>
            <div className="border border-[#E5E7EB] rounded p-3">
              <p className="text-[10px] uppercase tracking-wider text-[#9CA3AF] mb-1">Webhook Secret</p>
              <p className={`font-bold text-sm ${diagnostics.stripe.webhook_secret_configured ? 'text-green-600' : 'text-red-600'}`}>
                {diagnostics.stripe.webhook_secret_configured ? 'Configured' : 'Missing'}
              </p>
            </div>
            <div className="border border-[#E5E7EB] rounded p-3">
              <p className="text-[10px] uppercase tracking-wider text-[#9CA3AF] mb-1">Sender Email</p>
              <p className={`font-bold text-xs ${diagnostics.env.sender_email_is_default ? 'text-amber-500' : 'text-green-600'}`}>
                {diagnostics.env.sender_email || '—'}
              </p>
              {diagnostics.env.sender_email_is_default && (
                <p className="text-[9px] text-amber-600 mt-0.5">Using Resend dev fallback</p>
              )}
            </div>
          </div>

          {/* Expected webhook URL — copyable */}
          <div className="border border-[#E5E7EB] rounded p-3 bg-[#F8F9FA]">
            <div className="flex items-center justify-between mb-1">
              <p className="text-[10px] uppercase tracking-wider text-[#4B5563] font-semibold">Expected Webhook URL (paste into Stripe Dashboard)</p>
              <button onClick={() => copyToClipboard(diagnostics.stripe.expected_webhook_url)}
                className="text-[10px] text-blue-600 hover:text-blue-700 flex items-center gap-1"
                data-testid="copy-webhook-url-btn">
                <Copy className="w-3 h-3" /> Copy
              </button>
            </div>
            <code className="text-[11px] font-mono text-[#0A0A0A] break-all">{diagnostics.stripe.expected_webhook_url}</code>
            {!diagnostics.env.public_site_url && (
              <p className="text-[9px] text-amber-600 mt-1">
                ⚠️ `PUBLIC_SITE_URL` not set in .env — this shows the internal host. Set `PUBLIC_SITE_URL=https://novenahf.org` in production.
              </p>
            )}
          </div>

          {/* Email Alerts row */}
          <div className="border border-[#E5E7EB] rounded p-3 bg-[#F8F9FA] flex items-center justify-between gap-3 flex-wrap">
            <div className="text-xs text-[#4B5563]">
              <p className="font-semibold text-[#0A0A0A] mb-0.5 flex items-center gap-1.5">
                <Mail className="w-3.5 h-3.5 text-[#BA1826]" />
                Email Alerts
              </p>
              <p className="text-[10px]">
                Auto-emails admin when failure rate &gt; 10% or webhook outage sustained &gt; 5 min. 30-min cooldown between alerts.
              </p>
            </div>
            <button onClick={sendTestAlert}
              className="text-[10px] bg-[#0A0A0A] text-white rounded px-3 py-1.5 hover:bg-[#1a1a1a] flex items-center gap-1"
              data-testid="send-test-alert-btn">
              <Mail className="w-3 h-3" /> Send Test Alert
            </button>
          </div>

          {/* Daily Digest row */}
          <div className="border border-[#E5E7EB] rounded p-3 bg-[#F8F9FA] flex items-center justify-between gap-3 flex-wrap">
            <div className="text-xs text-[#4B5563]">
              <p className="font-semibold text-[#0A0A0A] mb-0.5 flex items-center gap-1.5">
                <BarChart3 className="w-3.5 h-3.5 text-[#0A0A0A]" />
                Daily Admin Digest
              </p>
              <p className="text-[10px]">
                Sent automatically every day at 7:00 AM UTC. Summary of yesterday's donations, RSVPs, top contributor, and stale-pending alerts.
              </p>
            </div>
            <button onClick={sendTestDigest}
              className="text-[10px] bg-[#BA1826] text-white rounded px-3 py-1.5 hover:bg-[#a01420] flex items-center gap-1"
              data-testid="send-test-digest-btn">
              <Mail className="w-3 h-3" /> Send Test Digest
            </button>
          </div>

          {/* Checklist */}
          <div>
            <p className="text-[10px] uppercase tracking-wider text-[#4B5563] font-semibold mb-2">Deployment Checks</p>
            <ul className="space-y-1">
              {Object.entries(diagnostics.readiness.checks).map(([k, v]) => (
                <li key={k} className="flex items-center gap-2 text-xs" data-testid={`diag-check-${k}`}>
                  {v ? <CheckCircle className="w-3.5 h-3.5 text-green-600 flex-shrink-0" />
                    : <XCircle className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />}
                  <span className={v ? 'text-[#0A0A0A]' : 'text-[#4B5563]'}>
                    {k.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* Webhook activity */}
          <div>
            <p className="text-[10px] uppercase tracking-wider text-[#4B5563] font-semibold mb-2">Webhook Activity</p>

            {/* Health alert banner */}
            {diagnostics.webhook_activity.health_alert && (
              <div className="bg-red-50 border-2 border-red-300 text-red-800 rounded p-3 mb-3 flex items-start gap-2" data-testid="webhook-health-alert">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <div className="text-xs">
                  <p className="font-bold">⚠ Possible webhook outage</p>
                  <p>{diagnostics.donations.stale_pending_older_than_1h} donations have been pending &gt; 1 hour and no webhook has succeeded recently. Check Stripe Dashboard → Webhooks → recent deliveries.</p>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-center mb-3">
              <div className="border border-[#E5E7EB] rounded p-2">
                <div className="text-lg font-bold text-[#0A0A0A]">{diagnostics.webhook_activity.total_events_logged}</div>
                <p className="text-[9px] text-[#4B5563]">Total Logged</p>
              </div>
              <div className="border border-green-200 bg-green-50 rounded p-2">
                <div className="text-lg font-bold text-green-700">{diagnostics.webhook_activity.processed_count || 0}</div>
                <p className="text-[9px] text-green-600 font-semibold">Processed</p>
              </div>
              <div className={`border rounded p-2 ${diagnostics.webhook_activity.failed_count > 0 ? 'border-red-300 bg-red-50' : 'border-[#E5E7EB]'}`}>
                <div className={`text-lg font-bold ${diagnostics.webhook_activity.failed_count > 0 ? 'text-red-700' : 'text-[#9CA3AF]'}`} data-testid="webhook-failed-count">
                  {diagnostics.webhook_activity.failed_count || 0}
                </div>
                <p className="text-[9px] text-red-600 font-semibold">Failed</p>
              </div>
              <div className={`border rounded p-2 ${diagnostics.webhook_activity.rejected_count > 0 ? 'border-red-300 bg-red-50' : 'border-[#E5E7EB]'}`}>
                <div className={`text-lg font-bold ${diagnostics.webhook_activity.rejected_count > 0 ? 'text-red-700' : 'text-[#9CA3AF]'}`}>
                  {diagnostics.webhook_activity.rejected_count || 0}
                </div>
                <p className="text-[9px] text-red-600 font-semibold">Rejected (bad sig)</p>
              </div>
              <div className="border border-amber-200 bg-amber-50 rounded p-2">
                <div className="text-lg font-bold text-amber-600">{diagnostics.donations.stale_pending_older_than_1h}</div>
                <p className="text-[9px] text-amber-700 font-semibold">Stale Pending (&gt;1h)</p>
              </div>
            </div>

            {diagnostics.webhook_activity.failed_count > 0 && (
              <div className="bg-red-50 border border-red-200 rounded p-2 mb-3 text-[10px] text-red-800" data-testid="webhook-last-failure">
                <p className="font-semibold">Last failure: {diagnostics.webhook_activity.last_failed_at ? formatDateTime(diagnostics.webhook_activity.last_failed_at) : '—'}</p>
                <p className="font-mono text-[9px] mt-0.5 break-all">{diagnostics.webhook_activity.last_failure_error || '—'}</p>
                <p className="text-[9px] mt-1 italic">Failure rate: {diagnostics.webhook_activity.failure_rate_pct || 0}% of checkout events. Review Stale Pending donations and use "Verify with Stripe" on each to recover.</p>
              </div>
            )}

            {diagnostics.webhook_activity.last_event_at ? (
              <p className="text-[10px] text-[#4B5563]">
                Last event: <span className="font-mono">{diagnostics.webhook_activity.last_event_type}</span>
                {' '}at {formatDateTime(diagnostics.webhook_activity.last_event_at)}
                {diagnostics.webhook_activity.last_event_verified === false && (
                  <span className="ml-2 text-red-600 font-semibold">(signature NOT verified)</span>
                )}
              </p>
            ) : (
              <p className="text-[10px] text-amber-600 italic">
                No webhook events received yet. Once you configure the webhook in Stripe Dashboard, send a test event and click "Webhook Diagnostics" again.
              </p>
            )}
          </div>

          {/* Recent events table (compact) */}
          {diagnostics.webhook_activity.recent_events?.length > 0 && (
            <div className="border border-[#E5E7EB] rounded overflow-hidden">
              <table className="w-full text-[10px]">
                <thead className="bg-[#F8F9FA]">
                  <tr>
                    <th className="text-left py-1.5 px-2 font-semibold">When</th>
                    <th className="text-left py-1.5 px-2 font-semibold">Type</th>
                    <th className="text-center py-1.5 px-2 font-semibold">Status</th>
                    <th className="text-center py-1.5 px-2 font-semibold">Verified</th>
                    <th className="text-right py-1.5 px-2 font-semibold">Amount</th>
                    <th className="text-left py-1.5 px-2 font-semibold">Session / Error</th>
                  </tr>
                </thead>
                <tbody>
                  {diagnostics.webhook_activity.recent_events.slice(0, 10).map((e, i) => {
                    const status = e.processing_status || 'received';
                    const statusColor = status === 'processed' ? 'text-green-600 bg-green-50' :
                      status === 'failed' || status === 'rejected' ? 'text-red-700 bg-red-50' :
                      status === 'skipped' ? 'text-[#9CA3AF] bg-gray-50' : 'text-[#4B5563] bg-[#F8F9FA]';
                    return (
                      <tr key={i} className="border-t border-[#F1F5F9]" data-testid={`webhook-event-row-${i}`}>
                        <td className="py-1 px-2 text-[#4B5563] whitespace-nowrap">{formatDateTime(e.received_at)}</td>
                        <td className="py-1 px-2 font-mono text-[#0A0A0A]">{e.event_type}</td>
                        <td className="py-1 px-2 text-center">
                          <span className={`inline-block text-[8px] font-semibold px-1.5 py-0.5 rounded ${statusColor}`}>{status}</span>
                        </td>
                        <td className="py-1 px-2 text-center">{e.signature_verified
                          ? <CheckCircle className="w-3 h-3 text-green-600 inline" />
                          : <XCircle className="w-3 h-3 text-red-500 inline" />}</td>
                        <td className="py-1 px-2 text-right font-semibold">{e.amount_total ? `$${parseFloat(e.amount_total).toFixed(2)}` : '—'}</td>
                        <td className="py-1 px-2 font-mono text-[9px] text-[#9CA3AF] break-all">
                          {e.processing_error
                            ? <span className="text-red-600">{e.processing_error.substring(0, 60)}{e.processing_error.length > 60 ? '…' : ''}</span>
                            : (e.session_id ? e.session_id.substring(0, 24) + '…' : '—')}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <p className="text-[9px] text-[#9CA3AF] italic">
            Generated {formatDateTime(diagnostics.generated_at)}. See <code>/app/DEPLOY_CHECKLIST.md</code> for the full production checklist.
          </p>
        </div>
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-4 text-center">
          <DollarSign className="w-5 h-5 text-[#BA1826] mx-auto mb-1" />
          <div className="font-heading text-xl font-bold text-[#0A0A0A]">
            ${donations.filter(d => d.status === 'completed').reduce((s, d) => s + (d.amount || 0), 0).toLocaleString()}
          </div>
          <p className="text-[10px] text-[#4B5563]">Total Received</p>
        </div>
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-4 text-center">
          <CheckCircle className="w-5 h-5 text-green-600 mx-auto mb-1" />
          <div className="font-heading text-xl font-bold text-[#0A0A0A]">{donations.filter(d => d.status === 'completed').length}</div>
          <p className="text-[10px] text-[#4B5563]">Completed</p>
        </div>
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-4 text-center">
          <Clock className="w-5 h-5 text-amber-500 mx-auto mb-1" />
          <div className="font-heading text-xl font-bold text-[#0A0A0A]">{donations.filter(d => d.status === 'pending').length}</div>
          <p className="text-[10px] text-[#4B5563]">Pending</p>
        </div>
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-4 text-center">
          <XCircle className="w-5 h-5 text-red-500 mx-auto mb-1" />
          <div className="font-heading text-xl font-bold text-[#0A0A0A]">{donations.filter(d => ['failed','cancelled','refunded'].includes(d.status)).length}</div>
          <p className="text-[10px] text-[#4B5563]">Failed / Refunded</p>
        </div>
      </div>

      {/* Kits Funded This Month — per-kit analytics from dedication_kit metadata */}
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-4" data-testid="kit-analytics-block">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Gift className="w-4 h-4 text-[#BA1826]" />
            <h3 className="font-heading text-sm font-bold text-[#0A0A0A]">Kits Funded This Month</h3>
          </div>
          <span className="text-[9px] uppercase tracking-wider text-[#9CA3AF]">All-time totals shown below</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          {['mens', 'womens', 'childrens'].map((k) => (
            <div key={k} className="border border-[#E5E7EB] rounded-lg p-3" data-testid={`kit-stat-${k}`}>
              <div className="flex items-center gap-1.5 mb-1">
                <span className={`w-1.5 h-1.5 rounded-full ${KIT_META[k].dot}`} />
                <p className="text-[10px] font-semibold text-[#4B5563] uppercase tracking-wide leading-tight">{KIT_META[k].label}</p>
              </div>
              <div className={`font-heading text-xl font-bold ${KIT_META[k].color}`} data-testid={`kit-stat-${k}-count`}>
                {kitStats.monthly[k].count}
              </div>
              <p className="text-[10px] text-[#4B5563]">
                ${kitStats.monthly[k].revenue.toFixed(2)} this month
              </p>
              <p className="text-[9px] text-[#9CA3AF] mt-0.5">
                All-time: {kitStats.allTime[k].count} • ${kitStats.allTime[k].revenue.toFixed(2)}
              </p>
            </div>
          ))}
          <div className="border border-[#E5E7EB] rounded-lg p-3 bg-[#FEF9E7]" data-testid="kit-stat-total">
            <p className="text-[10px] font-semibold text-[#4B5563] uppercase tracking-wide mb-1">Total Kit Donations</p>
            <div className="font-heading text-xl font-bold text-[#0A0A0A]" data-testid="kit-stat-total-count">
              {kitStats.monthlyTotal.count}
            </div>
            <p className="text-[10px] text-[#4B5563]">this month</p>
            <p className="text-[9px] text-[#9CA3AF] mt-0.5">All-time: {kitStats.allTimeTotal.count}</p>
          </div>
          <div className="border border-[#BA1826]/40 rounded-lg p-3 bg-[#FEF2F2]" data-testid="kit-stat-revenue">
            <p className="text-[10px] font-semibold text-[#BA1826] uppercase tracking-wide mb-1">Total Kit Revenue</p>
            <div className="font-heading text-xl font-bold text-[#BA1826]" data-testid="kit-stat-revenue-amount">
              ${kitStats.monthlyTotal.revenue.toFixed(2)}
            </div>
            <p className="text-[10px] text-[#4B5563]">this month</p>
            <p className="text-[9px] text-[#9CA3AF] mt-0.5">All-time: ${kitStats.allTimeTotal.revenue.toFixed(2)}</p>
          </div>
        </div>
        {kitStats.allTimeTotal.count === 0 && (
          <p className="text-[10px] text-[#9CA3AF] mt-2 italic">
            No kit-specific donations yet. Kit attribution is captured when donors click "Fund a Kit" buttons on the Programs page.
          </p>
        )}
      </div>

      {/* Filters + Search */}
      <div className="flex gap-2 flex-wrap items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, email, ID, source..."
            className="w-full pl-9 pr-3 py-2 border border-[#E5E7EB] rounded text-xs focus:border-[#BA1826] focus:outline-none"
            data-testid="donation-search" />
        </div>
        <select value={filter} onChange={(e) => setFilter(e.target.value)}
          className="px-3 py-2 border border-[#E5E7EB] rounded text-xs focus:border-[#BA1826] focus:outline-none"
          data-testid="donation-filter">
          <option value="all">All Donations ({donations.length})</option>
          <option value="completed">Completed ({donations.filter(d => d.status === 'completed').length})</option>
          <option value="pending">Pending ({donations.filter(d => d.status === 'pending').length})</option>
          <option value="expired">Expired ({donations.filter(d => d.status === 'expired').length})</option>
          <option value="failed">Failed / Refunded ({donations.filter(d => ['failed','cancelled','refunded'].includes(d.status)).length})</option>
          <option value="test">Suspected Test ({donations.filter(d => (d.donor_name||'').toLowerCase().includes('test') || (d.donor_email||'').includes('example.com')).length})</option>
          <option disabled>──── Hygiene Kits ────</option>
          <option value="kit_all">All Kits ({donations.filter(d => !!classifyKit(d)).length})</option>
          <option value="kit_mens">Men's Kit ({donations.filter(d => classifyKit(d) === 'mens').length})</option>
          <option value="kit_womens">Women's Kit ({donations.filter(d => classifyKit(d) === 'womens').length})</option>
          <option value="kit_childrens">Children's Kit ({donations.filter(d => classifyKit(d) === 'childrens').length})</option>
        </select>
      </div>

      {/* Donation Table */}
      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[#BA1826]" /></div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-8 text-center">
          <DollarSign className="w-8 h-8 text-[#9CA3AF] mx-auto mb-2" />
          <p className="text-sm text-[#4B5563]">{donations.length === 0 ? 'No donations recorded yet.' : 'No donations match your filter.'}</p>
        </div>
      ) : (
        <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-[#F8F9FA] border-b border-[#E5E7EB]">
              <tr>
                <th className="text-left py-2 px-3 font-semibold" title={`Time zone: ${tzAbbrev()}`}>Date ({tzAbbrev()})</th>
                <th className="text-left py-2 px-3 font-semibold">Donor</th>
                <th className="text-left py-2 px-3 font-semibold">Email</th>
                <th className="text-right py-2 px-3 font-semibold">Amount</th>
                <th className="text-center py-2 px-3 font-semibold">Status</th>
                <th className="text-left py-2 px-3 font-semibold">Source</th>
                <th className="text-left py-2 px-3 font-semibold">Allocation</th>
                <th className="text-left py-2 px-3 font-semibold hidden lg:table-cell">Reference</th>
                <th className="text-center py-2 px-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(d => {
                const isTest = (d.donor_name || '').toLowerCase().includes('test') || (d.donor_email || '').includes('example.com');
                return (
                  <tr key={d.id} className={`border-b border-[#F1F5F9] last:border-0 ${isTest ? 'bg-red-50' : ''}`} data-testid={`donation-row-${d.id}`}>
                    <td className="py-2 px-3 text-[#4B5563] whitespace-nowrap" title={d.created_at ? formatDateTime(d.created_at) : ''}>{d.created_at ? formatDate(d.created_at) : '—'}</td>
                    <td className="py-2 px-3 font-medium">
                      {isTest && <AlertCircle className="w-3 h-3 text-red-500 inline mr-1" />}
                      {d.donor_name || 'Anonymous'}
                    </td>
                    <td className="py-2 px-3 text-[#4B5563]">
                      {d.donor_email || '—'}
                      {d.email_flagged && <span className="ml-1 text-[8px] bg-red-100 text-red-600 px-1 py-0.5 rounded font-semibold">Invalid</span>}
                      {d.email_excluded && <span className="ml-1 text-[8px] bg-gray-100 text-gray-500 px-1 py-0.5 rounded font-semibold">Excluded</span>}
                    </td>
                    <td className="py-2 px-3 text-right font-semibold text-[#BA1826]">${(d.amount || 0).toFixed(2)}</td>
                    <td className="py-2 px-3 text-center"><StatusBadge status={d.status} /></td>
                    <td className="py-2 px-3 text-[#4B5563]">{d.source_label || '—'}</td>
                    <td className="py-2 px-3 text-[#4B5563]">
                      {d.allocation || '—'}
                      {(() => {
                        const k = classifyKit(d);
                        return k ? (
                          <span className={`ml-1 inline-flex items-center gap-1 text-[8px] font-semibold px-1.5 py-0.5 rounded border ${k === 'mens' ? 'border-blue-400 text-blue-600 bg-blue-50' : k === 'womens' ? 'border-pink-400 text-pink-600 bg-pink-50' : 'border-green-500 text-green-700 bg-green-50'}`} data-testid={`row-kit-badge-${d.id}`}>
                            <Gift className="w-2.5 h-2.5" />{KIT_META[k].label.replace(' Hygiene Kits', '')}
                          </span>
                        ) : null;
                      })()}
                    </td>
                    <td className="py-2 px-3 text-[#9CA3AF] hidden lg:table-cell font-mono text-[9px]">{d.session_id ? d.session_id.substring(0, 20) + '...' : d.id}</td>
                    <td className="py-2 px-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        {d.status === 'pending' && (
                          <>
                            <button onClick={() => handleVerifyStripe(d.id)} disabled={actionLoading === `verify-${d.id}`}
                              className="text-blue-500 hover:text-blue-700 disabled:opacity-30" title="Verify with Stripe">
                              {actionLoading === `verify-${d.id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle className="w-3.5 h-3.5" />}
                            </button>
                            {d.donor_email && !d.reminder_sent_at && (
                              <button onClick={() => handleSendReminder(d.id)} disabled={actionLoading === `remind-${d.id}`}
                                className="text-amber-500 hover:text-amber-700 disabled:opacity-30" title="Send reminder email">
                                {actionLoading === `remind-${d.id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
                              </button>
                            )}
                            {d.reminder_sent_at && (
                              <span className="text-[8px] text-green-600" title={`Sent ${formatDateTime(d.reminder_sent_at)}`}>Sent</span>
                            )}
                          </>
                        )}
                        {(d.status === 'paid' || d.status === 'completed') && (
                          <button onClick={() => handleResendReceipt(d)}
                            disabled={actionLoading === `resend-${d.id}`}
                            className="text-[#BA1826] hover:text-[#9E1420] disabled:opacity-30"
                            title={d.donor_email
                              ? `Email the receipt to ${d.donor_email}`
                              : 'No email on file — click to enter one and send'}
                            data-testid={`resend-receipt-${d.id}`}>
                            {actionLoading === `resend-${d.id}`
                              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              : <Mail className="w-3.5 h-3.5" />}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-[10px] text-[#9CA3AF]">Showing {filtered.length} of {donations.length} total records</p>
    </div>
  );
};

export default AdminDonationsTab;
